import {PayloadAction} from "@reduxjs/toolkit";
import {call, cancel, fork, put, race, select, take} from 'redux-saga/effects'
import {GroupObject} from "../../../../../vkapi/objects/GroupObject";
import {convert_urls_to_id} from "../../../../../vkapi/tasks/ConverUrlToId";
import GetGroups from "../../../../../vkapi/tasks/GetGroups";
import {showAlertError, showAlertParsingCompleted} from "../../../../app/actions";
import ProgressGenerator from "../../../../helpers/ProgressGenerator";
import {RootState, TaskStatus} from "../../../types";
import {audience as actions} from "../actions";
import GetStatsQuick from "../tasks/GetStatsQuick";
import GetStatsSlow from "../tasks/GetStatsSlow";
import {AudienceFilterResult, CommunitiesFilterState, FilterSearchType} from "../types";

const main = function* () {
    while (yield take(actions.start)) {
        yield put(actions.setTaskStatus(TaskStatus.RUNNING));
        const task = yield fork(start);
        yield race({
            stop: call(onStop, task),
            completed: call(onCompleted),
            onError: call(onError, task),
        });
        yield put(actions.setTaskStatus(TaskStatus.READY));
    }
};

function* start() {
    try {
        yield execute();
    }
    catch (e) {
        yield put(actions.setTaskError(e))
    }
}

function* execute() {
    const state: CommunitiesFilterState['audience'] = yield select((state: RootState) => state.communitiesFilter.audience);
    yield validate(state);
    const access_token = yield select((state: RootState) => state.app.access_token);

    const group_ids: string[] = convert_urls_to_id(state.settings.source.split("\n"));

    const groups: GroupObject[] = yield GetGroups({
        group_ids: group_ids,
        fields: 'members_count',
        access_token: access_token,
        progress_action: actions.setProgress,
        add_api_errors_action: actions.addApiError,
    });

    const progress = ProgressGenerator({text: 'Обрабатываем группы', total: groups.length, action: actions.setProgress});

    const result_map: Map<number, AudienceFilterResult> = new Map<number, AudienceFilterResult>();

    for (const group of groups) {
        yield progress.next().value;
        if (!group.members_count) {
            continue;
        }
        if (state.settings.search_type === FilterSearchType.QUICK) {
            const stats: AudienceFilterResult = yield GetStatsQuick({
                group: group,
                access_token: access_token,
                search: {
                    age_min: state.settings.age_min,
                    age_max: state.settings.age_max,
                    country_id: state.settings.selected_country?.id || 0,
                    city_id: state.settings.selected_city?.id || 0,
                    gender: state.settings.gender,
                }
            });
            result_map.set(group.id, stats);
        }
        if (state.settings.search_type === FilterSearchType.SLOW) {
            try {
                const stats: AudienceFilterResult = yield GetStatsSlow({
                    group: group,
                    access_token: access_token,
                    search: {
                        age_min: state.settings.age_min,
                        age_max: state.settings.age_max,
                        country_id: state.settings.selected_country?.id || 0,
                        city_id: state.settings.selected_city?.id || 0,
                        gender: state.settings.gender,
                    }
                });
                result_map.set(group.id, stats);
            }
            catch (e) {
                continue;
            }
        }
    }

    yield put(actions.setResult(Array.from(result_map.values())));
    yield put(actions.setProgress({message: `Готово.`, total: 100, current: 100}));
    yield put(showAlertParsingCompleted(`Готово.`));
    yield put(actions.setCompleted());
}

function* validate(state: CommunitiesFilterState['audience']) {
    if (state.settings.source.trim() === '') {
        yield put(showAlertError('Вы не указали ссылки на сообщества'));
        yield put(actions.stop());
        return;
    }
}

function* onStop(task) {
    yield take(actions.stop);
    yield cancel(task);
}

function* onCompleted() {
    yield take(actions.setCompleted);
}

function* onError(task) {
    const error: PayloadAction<Error> = yield take(actions.setTaskError);
    yield put(showAlertError(error.payload.message));
    yield cancel(task);
}

export default main;
